import { getDatabase, ref, get, update, push } from 'firebase/database';
import { PhotoModel, ValueStatus } from '../types/protocols';
import { Unit, UnitCreateInput, UnitInput } from '../types/units';
import { UnitKind, UnitTenure } from '../types/Enums';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { isWholeUnitRoomType, unitDisplayName, wholeUnitRoomType } from '../Helpers/unitUtils';
import { RoomType } from '../types/Enums';
import { softDeleteFile } from './fileDeletionApi';
import { recordActivity } from './activityApi';
import { requireAdmin } from './adminGuard';
import type { CreatedRoomNode } from './roomsApi';

/** Tombstone entity type for unit photos. Shared with iOS FileDeletionHelper. */
export const UNIT_PHOTO_ENTITY_TYPE = 'unitPhoto';

const KINDS = Object.values(UnitKind) as string[];
const TENURES = Object.values(UnitTenure) as string[];
const COMMERCIAL_TYPE_MAX_LENGTH = 100;
const OFFICIAL_NUMBER_RE = /^\d{4}$/;

/** RTDB rejects `undefined`; drop those keys before writing. */
const stripUndefined = <T extends Record<string, unknown>>(value: T): T => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== undefined) {
            result[key] = entry;
        }
    }
    return result as T;
};

/** For `update()`: `undefined` becomes `null` so cleared fields are removed. */
const nullForUndefined = (value: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        result[key] = entry === undefined ? null : entry;
    }
    return result;
};

const optionalString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length > 0 ? value : undefined;

const optionalNumber = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** `{ key: true }` maps; anything else (or an empty map) is undefined. */
const trueMap = (value: unknown): { [key: string]: true } | undefined => {
    if (!value || typeof value !== 'object') return undefined;
    const result: { [key: string]: true } = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
        if (entry === true) result[key] = true;
    }
    return Object.keys(result).length > 0 ? result : undefined;
};

/**
 * Units (lägenheter and lokaler) at `buildings/{buildingKey}/units/{unitKey}`.
 * Reads need a logged-in user; every mutation needs an admin, like
 * storageUnitsApi. Units are updated with `update()` and explicit nulls so
 * photos written elsewhere are never clobbered. A unit is a group of rooms
 * (plan D5): it has no levels or pins of its own, so there is no blueprint
 * handling here beyond the whole-unit room created with a new unit (D13).
 * `currentLeaseKey` is written by leasesApi only (plan D7).
 */
export class UnitsApi {
    private db = getDatabase();

    getUnitsPath(buildingKey: string): string {
        return `buildings/${buildingKey}/units`;
    }

    getUnitPath(buildingKey: string, unitKey: string): string {
        return `${this.getUnitsPath(buildingKey)}/${unitKey}`;
    }

    /** DB path == Storage path for photos, like every other photo in the app. */
    getUnitPhotoPath(buildingKey: string, unitKey: string, photoKey: string): string {
        return `${this.getUnitPath(buildingKey, unitKey)}/photos/${photoKey}`;
    }

    // MARK: - Auth

    private async verifyAdmin(action: string): Promise<string> {
        return requireAdmin(this.db, action);
    }

    private stamp(uid: string): { updatedBy: string; updatedWhen: string } {
        return { updatedBy: uid, updatedWhen: formatDateForStorage(new Date()) };
    }

    // MARK: - Parsing

    parseUnit(data: unknown, key: string): Unit | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;
        // A unit without a kind cannot be shown anywhere; skip it rather than guess.
        if (typeof d.kind !== 'string' || !KINDS.includes(d.kind)) return null;
        const kind = d.kind as UnitKind;
        const tenure = typeof d.tenure === 'string' && TENURES.includes(d.tenure)
            ? (d.tenure as UnitTenure)
            : UnitTenure.Hyresratt;
        return stripUndefined({
            key,
            kind,
            tenure,
            number: typeof d.number === 'string' ? d.number : '',
            officialNumber: kind === UnitKind.Apartment ? optionalString(d.officialNumber) : undefined,
            area: optionalNumber(d.area),
            roomCount: kind === UnitKind.Apartment ? optionalNumber(d.roomCount) : undefined,
            commercialType: kind === UnitKind.Commercial ? optionalString(d.commercialType) : undefined,
            // Dangling room keys are the reader's problem; nothing to validate here. Absent = unplaced.
            rooms: trueMap(d.rooms),
            photos: d.photos && typeof d.photos === 'object'
                ? (d.photos as { [photoKey: string]: PhotoModel })
                : undefined,
            comment: optionalString(d.comment),
            stairwellKey: optionalString(d.stairwellKey),
            currentLeaseKey: optionalString(d.currentLeaseKey),
            createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
            createdWhen: typeof d.createdWhen === 'string' ? d.createdWhen : '',
            updatedBy: optionalString(d.updatedBy),
            updatedWhen: optionalString(d.updatedWhen),
        });
    }

    /** Parses the raw `units` node of a building model; invalid units are skipped. */
    parseUnits(data: unknown): { [unitKey: string]: Unit } {
        const units: { [unitKey: string]: Unit } = {};
        if (!data || typeof data !== 'object') return units;
        for (const [unitKey, unitData] of Object.entries(data as Record<string, unknown>)) {
            const unit = this.parseUnit(unitData, unitKey);
            if (unit) {
                units[unitKey] = unit;
            } else {
                console.warn(`Skipping invalid unit ${unitKey}`);
            }
        }
        return units;
    }

    // MARK: - Units

    /**
     * Room links, validated against the building: every room exists and is
     * held by no other unit. Best effort read-then-write; no conflict
     * handling. Returns the `rooms` map, or `undefined` for an empty list.
     */
    private async validateRooms(
        buildingKey: string,
        unitKey: string | null,
        roomKeys: string[]
    ): Promise<{ [roomKey: string]: true } | undefined> {
        const wanted = Array.from(new Set(roomKeys.map(k => k.trim()).filter(k => k.length > 0)));
        if (wanted.length === 0) return undefined;
        const [roomsSnapshot, unitsSnapshot] = await Promise.all([
            get(ref(this.db, `buildings/${buildingKey}/structure/rooms`)),
            get(ref(this.db, this.getUnitsPath(buildingKey))),
        ]);
        const rooms = roomsSnapshot.exists() ? (roomsSnapshot.val() as Record<string, Record<string, unknown>>) : {};
        const units = this.parseUnits(unitsSnapshot.exists() ? unitsSnapshot.val() : undefined);
        const result: { [roomKey: string]: true } = {};
        for (const roomKey of wanted) {
            const room = rooms[roomKey];
            if (!room || typeof room !== 'object') {
                throw new Error('Rummet finns inte');
            }
            const holder = Object.values(units).find(unit => unit.key !== unitKey && unit.rooms?.[roomKey] === true);
            if (holder) {
                throw new Error(`Rummet tillhör redan ${unitDisplayName(holder)}`);
            }
            result[roomKey] = true;
        }
        return result;
    }

    /**
     * Normalised, kind-consistent fields. Fields of the other kind come back as
     * `undefined` so an update nulls them (plan D11). Rooms are validated
     * separately (validateRooms) because they need the building.
     */
    private validateUnitInput(input: UnitInput): Omit<Unit, 'key' | 'createdBy' | 'createdWhen' | 'rooms' | 'photos' | 'currentLeaseKey'> {
        if (!KINDS.includes(input.kind)) {
            throw new Error('Ogiltig typ av enhet');
        }
        if (!TENURES.includes(input.tenure)) {
            throw new Error('Ogiltig upplåtelseform');
        }
        const number = (input.number || '').trim().toUpperCase();
        if (!number) {
            throw new Error(input.kind === UnitKind.Commercial ? 'Lokalnummer krävs' : 'Lägenhetsnummer krävs');
        }
        if (input.area !== undefined && !(input.area > 0)) {
            throw new Error('Yta måste vara större än 0');
        }
        const isApartment = input.kind === UnitKind.Apartment;
        const officialNumber = isApartment ? optionalString(input.officialNumber?.trim()) : undefined;
        if (officialNumber !== undefined && !OFFICIAL_NUMBER_RE.test(officialNumber)) {
            throw new Error('Lägenhetsnummer enligt Lantmäteriet är fyra siffror, t.ex. 1101');
        }
        const roomCount = isApartment ? input.roomCount : undefined;
        if (roomCount !== undefined && !(Number.isInteger(roomCount) && roomCount > 0)) {
            throw new Error('Antal rum måste vara ett positivt heltal');
        }
        const commercialType = !isApartment ? optionalString(input.commercialType?.trim()) : undefined;
        if (commercialType !== undefined && commercialType.length > COMMERCIAL_TYPE_MAX_LENGTH) {
            throw new Error(`Typ av lokal får vara högst ${COMMERCIAL_TYPE_MAX_LENGTH} tecken`);
        }
        return {
            kind: input.kind,
            tenure: input.tenure,
            number,
            officialNumber,
            area: input.area === undefined ? undefined : Math.round(input.area * 10) / 10,
            roomCount,
            commercialType,
            comment: optionalString(input.comment?.trim()),
            stairwellKey: optionalString(input.stairwellKey),
        };
    }

    /** Best effort, like validateRooms: the stairwell must still exist. */
    private async validateStairwell(buildingKey: string, stairwellKey: string | undefined): Promise<void> {
        if (!stairwellKey) return;
        const snapshot = await get(ref(this.db, `buildings/${buildingKey}/stairwells/${stairwellKey}`));
        if (!snapshot.exists()) {
            throw new Error('Trappuppgången finns inte längre');
        }
    }

    /**
     * Creates a unit. With `roomKeys` the unit links those rooms. Without
     * rooms but with a `placement` (a click on a level's drawing) a whole-unit
     * room is created on that level at the click, in the same root `update()`;
     * the room node has the same shape as roomsApi.createRoom / iOS RoomWriter.
     * Without either the unit is created unplaced (no `rooms`).
     */
    /** Every unit of a building, keyed by unit key; empty when there are none. */
    async getUnits(buildingKey: string): Promise<{ [unitKey: string]: Unit }> {
        const snapshot = await get(ref(this.db, this.getUnitsPath(buildingKey)));
        return this.parseUnits(snapshot.exists() ? snapshot.val() : undefined);
    }

    async createUnit(buildingKey: string, input: UnitCreateInput): Promise<Unit> {
        const uid = await this.verifyAdmin('skapa enhet');
        const validated = this.validateUnitInput(input);
        await this.validateStairwell(buildingKey, validated.stairwellKey);

        const newRef = push(ref(this.db, this.getUnitsPath(buildingKey)));
        const key = newRef.key;
        if (!key) {
            throw new Error('Kunde inte skapa nyckel för enheten');
        }

        const updates: Record<string, unknown> = {};
        let rooms = await this.validateRooms(buildingKey, null, input.roomKeys || []);
        if (!rooms && input.placement) {
            const levelKey = input.placement.levelKey.trim();
            const levelSnapshot = await get(ref(this.db, `buildings/${buildingKey}/structure/levels/${levelKey}`));
            if (!levelSnapshot.exists()) {
                throw new Error('Våningen finns inte längre');
            }
            const currentBlueprintKey = (levelSnapshot.val() as Record<string, unknown>).currentBlueprintKey;
            if (typeof currentBlueprintKey !== 'string' || currentBlueprintKey.length === 0) {
                throw new Error('Våningen har ingen ritning att placera rummet på');
            }
            const roomKey = push(ref(this.db, `buildings/${buildingKey}/structure/rooms`)).key;
            if (!roomKey) {
                throw new Error('Kunde inte skapa nyckel för rummet');
            }
            const room: CreatedRoomNode & { blueprintLocations: Record<string, unknown> } = {
                key: roomKey,
                levelKey,
                type: wholeUnitRoomType(validated.kind),
                number: validated.number,
                ceilingHeight: { status: ValueStatus.notSet },
                areaShapes: { status: ValueStatus.notSet },
                blueprintLocations: { [currentBlueprintKey]: { ...input.placement.location, key: currentBlueprintKey } },
            };
            updates[`buildings/${buildingKey}/structure/rooms/${roomKey}`] = room;
            rooms = { [roomKey]: true };
        }

        const unit: Unit = stripUndefined({
            key,
            ...validated,
            rooms,
            createdBy: uid,
            createdWhen: formatDateForStorage(new Date()),
        });
        updates[this.getUnitPath(buildingKey, key)] = unit;
        await update(ref(this.db), updates);
        recordActivity('unit.created', { buildingKey, targetKey: key, label: unit.number });
        return unit;
    }

    /**
     * Rewrites the editable fields. The other kind's fields are nulled (D11)
     * and `rooms` is replaced as a whole (null when empty: the unit becomes
     * unplaced); photos and currentLeaseKey are untouched. Tenure cannot leave hyresrätt while a
     * lease holds the unit: the lease and the reconcile job would keep the
     * unit rented while the UI hides it (D3).
     */
    async updateUnit(buildingKey: string, unitKey: string, input: UnitInput): Promise<void> {
        const uid = await this.verifyAdmin('ändra enhet');
        const validated = this.validateUnitInput(input);
        const snapshot = await get(ref(this.db, this.getUnitPath(buildingKey, unitKey)));
        const existing = snapshot.exists() ? this.parseUnit(snapshot.val(), unitKey) : null;
        if (!existing) {
            throw new Error('Enheten hittades inte');
        }
        if (validated.tenure !== UnitTenure.Hyresratt && optionalString(existing.currentLeaseKey)) {
            throw new Error('Enheten har ett hyresavtal – avsluta det först');
        }
        await this.validateStairwell(buildingKey, validated.stairwellKey);
        const rooms = await this.validateRooms(buildingKey, unitKey, input.roomKeys || []);
        await update(ref(this.db, this.getUnitPath(buildingKey, unitKey)), {
            ...nullForUndefined({ ...validated }),
            rooms: rooms ?? null,
            ...this.stamp(uid),
        });
        recordActivity('unit.updated', { buildingKey, targetKey: unitKey, label: validated.number });
    }

    /**
     * Tombstones every photo, then removes the unit node. Per file and not
     * atomic — accepted for parking and storage units and kept here. Refused
     * while the unit holds a lease (plan D12); ended leases never block.
     * Whole-unit rooms (D13) that carry nothing — no components pointing at
     * them, no photos, no parking area — go with the unit in the same write;
     * every other room stays in the structure, unlinked.
     */
    async deleteUnit(buildingKey: string, unitKey: string): Promise<void> {
        await this.verifyAdmin('ta bort enhet');
        const unitPath = this.getUnitPath(buildingKey, unitKey);
        const snapshot = await get(ref(this.db, unitPath));
        if (!snapshot.exists()) {
            throw new Error('Enheten hittades inte');
        }
        const raw = snapshot.val() as Record<string, unknown>;
        if (optionalString(raw.currentLeaseKey)) {
            throw new Error('Enheten har ett aktivt hyresavtal');
        }
        const photos = raw.photos && typeof raw.photos === 'object' ? (raw.photos as Record<string, unknown>) : {};
        for (const photoKey of Object.keys(photos)) {
            await softDeleteFile({
                originalPath: this.getUnitPhotoPath(buildingKey, unitKey, photoKey),
                entityType: UNIT_PHOTO_ENTITY_TYPE,
            });
        }
        const updates: Record<string, unknown> = { [unitPath]: null };
        for (const roomKey of Object.keys(trueMap(raw.rooms) || {})) {
            if (await this.isEmptyWholeUnitRoom(buildingKey, roomKey)) {
                updates[`buildings/${buildingKey}/structure/rooms/${roomKey}`] = null;
            }
        }
        await update(ref(this.db), updates);
        recordActivity('unit.deleted', { buildingKey, targetKey: unitKey, label: optionalString(raw.number) });
    }

    /** A whole-unit room (D13) with no photos, no component pointing at it and no parking area in it. */
    private async isEmptyWholeUnitRoom(buildingKey: string, roomKey: string): Promise<boolean> {
        const roomSnapshot = await get(ref(this.db, `buildings/${buildingKey}/structure/rooms/${roomKey}`));
        if (!roomSnapshot.exists()) return false;
        const room = roomSnapshot.val() as Record<string, unknown>;
        if (!isWholeUnitRoomType(typeof room.type === 'string' ? (room.type as RoomType) : undefined)) return false;
        if (room.photos && typeof room.photos === 'object' && Object.keys(room.photos as object).length > 0) return false;
        const [componentsSnapshot, parkingSnapshot] = await Promise.all([
            get(ref(this.db, `buildings/${buildingKey}/structure/components`)),
            get(ref(this.db, `buildings/${buildingKey}/parking`)),
        ]);
        const components = componentsSnapshot.exists() ? (componentsSnapshot.val() as Record<string, Record<string, unknown>>) : {};
        const referencesRoom = (value: unknown): boolean =>
            value === roomKey || (!!value && typeof value === 'object' && (value as { value?: unknown }).value === roomKey);
        if (Object.values(components).some(component => component && referencesRoom(component.roomKey))) return false;
        const areas = parkingSnapshot.exists() ? (parkingSnapshot.val() as Record<string, Record<string, unknown>>) : {};
        if (Object.values(areas).some(area => area && area.roomKey === roomKey)) return false;
        return true;
    }

    // MARK: - Photos

    /** Guard + path config for usePhotoUpload; the hook does the actual upload. */
    async prepareUnitPhotoUpload(
        buildingKey: string,
        unitKey: string
    ): Promise<{ storagePath: (photoKey: string) => string; dbPath: (photoKey: string) => string }> {
        await this.verifyAdmin('ladda upp foton');
        const path = (photoKey: string) => this.getUnitPhotoPath(buildingKey, unitKey, photoKey);
        return { storagePath: path, dbPath: path };
    }

    async stampUnitUpdated(buildingKey: string, unitKey: string): Promise<void> {
        const uid = await this.verifyAdmin('ändra enhet');
        await update(ref(this.db, this.getUnitPath(buildingKey, unitKey)), this.stamp(uid));
    }

    async deleteUnitPhoto(buildingKey: string, unitKey: string, photoKey: string): Promise<void> {
        const uid = await this.verifyAdmin('ta bort foton');
        const unitPath = this.getUnitPath(buildingKey, unitKey);
        await softDeleteFile({
            originalPath: this.getUnitPhotoPath(buildingKey, unitKey, photoKey),
            entityType: UNIT_PHOTO_ENTITY_TYPE,
            extraUpdates: {
                [`${unitPath}/updatedBy`]: uid,
                [`${unitPath}/updatedWhen`]: formatDateForStorage(new Date()),
            },
        });
    }
}

// Export singleton instance
export const unitsApi = new UnitsApi();
